import React, { useState } from 'react';
import Modal from '../../ui/Modal';

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZÑ'.split('');

// First letter of a name, accent-normalized (Á→A) but keeping Ñ, for A-Z jump.
const nameFirstLetter = (name) => {
  const ch = (name || '').trim().charAt(0);
  if (!ch) return '';
  const up = ch.toLocaleUpperCase('es');
  if (up === 'Ñ') return 'Ñ';
  return up.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
};

// ============================================
// ADD CLIENT MODAL - Quick client assignment
// ============================================
const AddClientModal = ({ isOpen, onClose, slot, clients, bookings, onAssign }) => {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [letterFilter, setLetterFilter] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);

  // Filter clients not already in this slot
  const slotClientIds = bookings
    .filter(b => b.time_slot_id === slot?.id && b.status !== 'cancelled')
    .map(b => b.client_id);

  // Base pool (before search / letter jump) — drives which letters are active.
  const baseClients = clients
    .filter(c => c.role === 'client' && !slotClientIds.includes(c.id))
    // Inactive clients are no longer active gym members — never bookable.
    .filter(c => (c.client_status || 'active') !== 'inactive')
    .filter(c => statusFilter === 'all' || (c.client_status || 'active') === statusFilter);

  const activeLetters = new Set(baseClients.map(c => nameFirstLetter(c.full_name)).filter(Boolean));

  const availableClients = baseClients
    .filter(c => !letterFilter || nameFirstLetter(c.full_name) === letterFilter)
    .filter(c =>
      search === '' ||
      c.full_name?.toLowerCase().includes(search.toLowerCase()) ||
      c.email?.toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => (a.full_name || '').localeCompare(b.full_name || '', 'es'))
    .slice(0, 50);

  const handleAssign = () => {
    if (selectedClient) {
      onAssign(slot, selectedClient);
      setSelectedClient(null);
      setSearch('');
      setLetterFilter('');
    }
  };

  if (!isOpen || !slot) return null;

  return (
    <Modal onClose={onClose} maxWidth="max-w-md">
      <Modal.Header onClose={onClose}>
        <h3 className="text-xl font-bold text-white">Assign client to slot</h3>
        <p className="text-sm text-slate-400 mt-1">
          {slot.date} • {slot.start_time} – {slot.end_time}
        </p>
      </Modal.Header>

      <Modal.Body>
        <div className="space-y-4">
          <input
            type="text"
            placeholder="Search client..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); if (e.target.value) setLetterFilter(''); }}
            className="input-field w-full"
            data-testid="client-search-input"
            autoFocus
          />

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="input-field w-full text-sm"
            data-testid="client-status-filter"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="onboarding">Onboarding</option>
          </select>

          {/* A-Z quick jump — tap a letter to filter by first name letter. */}
          <div className="flex flex-wrap gap-1" data-testid="client-letter-filter">
            <button
              type="button"
              onClick={() => setLetterFilter('')}
              className={`h-7 px-2 rounded text-xs font-semibold transition ${
                letterFilter === '' ? 'bg-blue-500 text-white' : 'bg-slate-700/50 text-slate-200 hover:bg-slate-600'
              }`}
              data-testid="letter-all"
            >
              All
            </button>
            {ALPHABET.map((L) => {
              const enabled = activeLetters.has(L);
              return (
                <button
                  key={L}
                  type="button"
                  disabled={!enabled}
                  onClick={() => { setLetterFilter(L); setSearch(''); }}
                  className={`w-7 h-7 rounded text-xs font-semibold flex items-center justify-center transition ${
                    letterFilter === L
                      ? 'bg-blue-500 text-white'
                      : enabled
                        ? 'bg-slate-700/50 text-slate-200 hover:bg-slate-600'
                        : 'bg-slate-800/40 text-slate-600 cursor-not-allowed'
                  }`}
                  data-testid={`letter-${L}`}
                >
                  {L}
                </button>
              );
            })}
          </div>

          <div className="max-h-60 overflow-y-auto space-y-1">
            {availableClients.map(client => (
              <div
                key={client.id}
                onClick={() => setSelectedClient(client)}
                data-testid={`assign-client-option-${client.id}`}
                className={`p-3 rounded cursor-pointer transition ${
                  selectedClient?.id === client.id
                    ? 'bg-blue-500/30 border border-blue-400'
                    : 'bg-slate-700/30 hover:bg-slate-600/30 border border-transparent'
                }`}
              >
                <div className="font-medium text-white">{client.full_name}</div>
                <div className="text-sm text-slate-400">{client.email}</div>
              </div>
            ))}

            {availableClients.length === 0 && (
              <div className="text-center text-slate-400 py-4">
                {search ? 'No clients found' : 'All clients already assigned'}
              </div>
            )}
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <button onClick={onClose} className="btn-secondary">Cancel</button>
        <button
          onClick={handleAssign}
          className="btn-primary"
          disabled={!selectedClient}
        >
          Assign client
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddClientModal;
